/**
 * useTheme — runtime light/dark theming for GonkaGame (Sprint B / TASK-B04).
 *
 * Model:
 *  - `preference`: the user's choice — 'light' | 'dark' | 'auto' (default 'light').
 *  - `resolvedTheme`: the concrete theme actually applied — 'light' | 'dark'
 *    ('auto' resolves to the OS `prefers-color-scheme`).
 *  - The resolved theme is written to `<html data-theme="...">`, which switches
 *    the semantic token values defined in `styles/tokens/_semantic.scss`.
 *
 * Persistence: localStorage key `gonkagame-theme`.
 * No-FOUC: an inline script in `index.html` applies `data-theme` before first
 * paint; `initTheme()` then syncs reactive state and attaches the OS listener.
 *
 * Module-scoped singleton state, so every `useTheme()` call shares one source.
 */
import { computed, ref, type ComputedRef } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'auto'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'gonkagame-theme'
const DEFAULT_PREFERENCE: ThemePreference = 'light'
const SYSTEM_QUERY = '(prefers-color-scheme: light)'

const preference = ref<ThemePreference>(DEFAULT_PREFERENCE)
const systemTheme = ref<ResolvedTheme>('dark')

let initialized = false
let mediaQuery: MediaQueryList | null = null

const resolvedTheme: ComputedRef<ResolvedTheme> = computed(() =>
  preference.value === 'auto' ? systemTheme.value : preference.value,
)

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored
    }
  } catch {
    /* storage unavailable (private mode / SSR) — fall through to default */
  }
  return DEFAULT_PREFERENCE
}

function detectSystemTheme(): ResolvedTheme {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia(SYSTEM_QUERY).matches ? 'light' : 'dark'
  }
  return 'dark'
}

function applyTheme(theme: ResolvedTheme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme)
  }
}

function handleSystemChange(event: MediaQueryListEvent): void {
  systemTheme.value = event.matches ? 'light' : 'dark'
  if (preference.value === 'auto') {
    applyTheme(resolvedTheme.value)
  }
}

/** Set the user's theme preference, persist it, and apply the resolved theme. */
function setTheme(value: ThemePreference): void {
  preference.value = value
  try {
    localStorage.setItem(THEME_STORAGE_KEY, value)
  } catch {
    /* ignore persistence failure */
  }
  applyTheme(resolvedTheme.value)
}

/** Flip between an explicit light/dark (used by the header toggle). */
function toggleTheme(): void {
  setTheme(resolvedTheme.value === 'dark' ? 'light' : 'dark')
}

/**
 * One-time initialization: read the stored preference, detect the OS theme,
 * attach the `prefers-color-scheme` listener, and apply the resolved theme.
 * Safe to call multiple times (no-op after the first).
 */
export function initTheme(): void {
  if (initialized) {
    return
  }
  initialized = true

  preference.value = readStoredPreference()
  systemTheme.value = detectSystemTheme()

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mediaQuery = window.matchMedia(SYSTEM_QUERY)
    mediaQuery.addEventListener?.('change', handleSystemChange)
  }

  applyTheme(resolvedTheme.value)
}

export interface UseThemeReturn {
  theme: ComputedRef<ThemePreference>
  resolvedTheme: ComputedRef<ResolvedTheme>
  isDark: ComputedRef<boolean>
  setTheme: (value: ThemePreference) => void
  toggleTheme: () => void
}

export function useTheme(): UseThemeReturn {
  return {
    theme: computed(() => preference.value),
    resolvedTheme,
    isDark: computed(() => resolvedTheme.value === 'dark'),
    setTheme,
    toggleTheme,
  }
}

/** Test-only: reset module singleton state between unit tests. */
export function __resetThemeForTests(): void {
  initialized = false
  preference.value = DEFAULT_PREFERENCE
  systemTheme.value = 'dark'
  if (mediaQuery) {
    mediaQuery.removeEventListener?.('change', handleSystemChange)
    mediaQuery = null
  }
}
